import { map, Observable } from 'rxjs';

import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Race } from '../models/race';
import { RaceClass } from '../models/race-class';
import { Registration } from '../models/registration';
import { UnsuccessRegistration, RaceProgressRegistrationDataRaw } from '../models';
import { SettingsService } from '../services/settings.service';
import { ServerResponse } from '../response';
import { User } from '../models/user';

@Injectable({
  providedIn: 'root',
})
export class RegistrationDomainService {
  constructor(
    private settingService: SettingsService,
    private http: HttpClient
  ) {}

  // Получает список команд участника
  getCrewList(rUid: string, clUid: string, cUid: string): Observable<any> {
    return this.http
      .get<ServerResponse<any>>(
        `${this.settingService.settings.urlGTM}/api/registration/crew/list?rUid=${rUid}&clUid=${clUid}&cUid=${cUid}`
      )
      .pipe(
        map((res: any) => res.data)
      );
  }

  // Создаёт команду участника
  createCrew(rUid: string, clUid: string, cUid: string, crewUid: string): Observable<any> {
    return this.http
      .post<ServerResponse<any>>(
        `${this.settingService.settings.urlGTM}/api/registration/crew`,
        {
          rUid,
          clUid,
          cUid,
          crewUid,
          lang: this.settingService.selectedLang,
        }
      )
      .pipe(
        map((res: any) => res.data)
      );
  }

  // Удаляет участника из команды
  deleteCrew(rUid: string, clUid: string, cUid: string, crewUid: string): Observable<void> {
    return this.http
      .delete<ServerResponse<any>>(
        `${this.settingService.settings.urlGTM}/api/registration/crew?rUid=${rUid}&clUid=${clUid}&cUid=${cUid}&crewUid=${crewUid}`
      )
      .pipe(
        map(() => undefined)
      );
  }

  // Регистрация участника
  register(race: Race, selectedClass: RaceClass): Observable<void> {
    return this.http
      .post(`${this.settingService.settings.urlGTM}/api/registration`, {
        rUid: race.rUid,
        clUid: selectedClass.clUid,
        lang: this.settingService.selectedLang,
      })
      .pipe(
        map(() => undefined)
      );
  }

  // Записывает регистрацию участника в гонку, класс с полными данными
  addRegistration(
    race: Race,
    selectedClass: RaceClass,
    user: User,
    startNumber: any,
    regInfo: any,
    files: any,
    answers: string[]
  ): Observable<void> {
    return this.http
      .post(`${this.settingService.settings.urlGTM}/api/registration`, {
        rUid: race.rUid,
        clUid: selectedClass.clUid,
        cUid: user.cUid,
        startNumber,
        regInfo,
        files,
        answers,
        lang: this.settingService.selectedLang,
      })
      .pipe(
        map(() => undefined)
      );
  }

  // Отмена регистрации участника
  deleteRegistration(rUid: string, cUid: string, clUid: string, erase: boolean): Observable<void> {
    return this.http
      .delete(
        `${this.settingService.settings.urlGTM}/api/registration?rUid=${rUid
        }&cUid=${cUid}&clUid=${clUid}&erase=${erase ? 1 : 0}`
      )
      .pipe(
        map(() => undefined)
      );
  }

  // Изменение данных регистрации участника
  updateRegistration(race: Race, user: any, selectedClass: RaceClass): Observable<void> {
    return this.http
      .patch(`${this.settingService.settings.urlGTM}/api/registration`, {
        rUid: race.rUid,
        cUid: user.cUid,
        clUid: selectedClass.clUid,
        info: user.info,
        lang: this.settingService.selectedLang,
      })
      .pipe(
        map(() => undefined)
      );
  }

  // Меняет класс участия
  changeRegistrationClass(
    rUid: string,
    cUid: string,
    clUidOld: string,
    clUidNew: string,
    startNumber: string,
    regInfo: string
  ): Observable<void> {
    return this.http
      .patch(`${this.settingService.settings.urlGTM}/api/registration`, {
        rUid,
        cUid,
        clUidOld,
        clUidNew,
        startNumber: startNumber || '',
        regInfo: regInfo || '',
        lang: this.settingService.selectedLang,
      })
      .pipe(
        map(() => undefined)
      );
  }

  // Логирование неудачной регистрации
  logUnsuccessRegistration(rUid: string, cUid: string, date: string, logMessage: string): Observable<void> {
    return this.http
      .post(`${this.settingService.settings.urlGTM}/api/registration/log_unsuccess`, {
        rUid,
        cUid,
        date,
        logMessage,
        lang: this.settingService.selectedLang,
      })
      .pipe(
        map(() => undefined)
      );
  }

  // Получает список неудачных регистраций
  getUnsuccessRegistrations(rUid: string, dateFrom: string, dateTo: string): Observable<UnsuccessRegistration[]> {
    return this.http
      .get<ServerResponse<any>>(
        `${this.settingService.settings.urlGTM}/api/registration/list_unsuccess?rUid=${rUid}&dateFrom=${dateFrom}&dateTo=${dateTo}&lang=${this.settingService.selectedLang}`
      )
      .pipe(
        map((response) => {
          return response.data.map((obj: any) => new UnsuccessRegistration(obj));
        })
      );
  }

  // Получает список участников гонки
  getRegistrationList(rUid: string, clUid: string): Observable<any> {
    return this.http
      .get(
        `${this.settingService.settings.urlGTM}/api/registration/list?rUid=${rUid}&clUid=${clUid}`
      )
      .pipe(
        map((res: any) => res.data)
      );
  }

  // Получает данные участника
  getParticipantData(rUid: string, clUid: string, cUid: string, deleted: boolean): Observable<Registration> {
    return this.http
      .get<ServerResponse<RaceProgressRegistrationDataRaw>>(`${this.settingService.settings.urlGTM}/api/registration/participant`, {
        params: {
          rUid,
          clUid,
          cUid,
          deleted: deleted ? 'deleted_reg' : '',
        },
      })
      .pipe(
        map((rawData) => new Registration(rawData.data))
      );
  }

  // Находит класс по номеру участника
  getClassByNumberCompetitor(rUid: string, cNumber: number): Observable<{ clUid: string; cNumber: number }> {
    return this.http
      .get<{ status: string; data: string }>(`${this.settingService.settings.urlGTM}/api/registration/class_by_competitor_number?rUid=${rUid}&cNumber=${cNumber.toString()}`)
      .pipe(
        map((retDataClassUid: { status: string; data: string }) => {
          return { clUid: retDataClassUid.data, cNumber };
        })
      );
  }

  // Сохраняет отметки документов участника
  saveDocuments(rUid: string, clUid: string, cUid: string, documents: any, startFee: number): Observable<void> {
    return this.http
      .post(`${this.settingService.settings.urlGTM}/api/registration/documents`, {
        rUid,
        clUid,
        cUid,
        docsJSON: documents,
        allDocumentsValid: documents.allDocumentsValid,
        payedValue: startFee,
        lang: this.settingService.selectedLang,
      })
      .pipe(
        map(() => undefined)
      );
  }

  // Сохраняет стартовый номер участника
  saveStartNumber(rUid: string, clUid: string, cUid: string, startNumber: string): Observable<void> {
    return this.http
      .patch(`${this.settingService.settings.urlGTM}/api/registration/start_number`, {
        rUid,
        clUid,
        cUid,
        startNumber,
        lang: this.settingService.selectedLang,
      })
      .pipe(
        map(() => undefined)
      );
  }

  // Сохраняет информацию о регистрации участника
  saveRegInfo(rUid: string, clUid: string, cUid: string, regInfo: any): Observable<void> {
    return this.http
      .patch(`${this.settingService.settings.urlGTM}/api/registration/reg_info`, {
        rUid,
        clUid,
        cUid,
        regInfo,
        lang: this.settingService.selectedLang,
      })
      .pipe(
        map(() => undefined)
      );
  }

  // Сохраняет стартовый взнос участника
  saveStartFee(rUid: string, clUid: string, cUid: string, startFee: number): Observable<void> {
    return this.http
      .patch(`${this.settingService.settings.urlGTM}/api/registration/start_fee`, {
        rUid,
        clUid,
        cUid,
        startFee,
        lang: this.settingService.selectedLang,
      })
      .pipe(
        map(() => undefined)
      );
  }

  // Сохраняет датчик участника
  saveChronoTag(rUid: string, clUid: string, cUid: string, chronoTag: number): Observable<void> {
    return this.http
      .patch(`${this.settingService.settings.urlGTM}/api/registration/chrono_tag`, {
        rUid,
        clUid,
        cUid,
        chronoTag,
        lang: this.settingService.selectedLang,
      })
      .pipe(
        map(() => undefined)
      );
  }

  // Сохраняет заметки оргa по участнику
  saveOrgComment(rUid: string, clUid: string, cUid: string, orgComment: string): Observable<void> {
    return this.http
      .patch(`${this.settingService.settings.urlGTM}/api/registration/org_comment`, {
        rUid,
        clUid,
        cUid,
        orgComment,
        lang: this.settingService.selectedLang,
      })
      .pipe(
        map(() => undefined)
      );
  }

  // Удаляет регистрацию участника (админ)
  deleteRegistrationAdmin(rUid: string, clUid: string, cUid: string): Observable<void> {
    return this.http
      .delete(
        `${this.settingService.settings.urlGTM}/api/registration?rUid=${rUid}&clUid=${clUid}&cUid=${cUid}&lang=${this.settingService.selectedLang}`
      )
      .pipe(
        map(() => undefined)
      );
  }

  // Присваивает номера участникам
  assignNumbers(rUid: string, clUid: string, variant: 'within_class' | 'across_all'): Observable<void> {
    return this.http
      .patch<ServerResponse<any>>(`${this.settingService.settings.urlGTM}/api/registration/numbers`, {
        rUid,
        clUid,
        variant,
        lang: this.settingService.selectedLang,
      })
      .pipe(
        map(() => undefined)
      );
  }

  // Удаляет номера участников
  deleteNumbers(rUid: string, clUid: string): Observable<void> {
    return this.http
      .delete<ServerResponse<any>>(
        `${this.settingService.settings.urlGTM}/api/registration/numbers?rUid=${rUid}&clUid=${clUid}&lang=${this.settingService.selectedLang}`
      )
      .pipe(
        map(() => undefined)
      );
  }

  // Изменяет время регистрации участника
  changeRegistrationTime(rUid: string, cUid: string, time: number): Observable<void> {
    return this.http
      .post(`${this.settingService.settings.urlGTM}/api/registration/change_reg_time`, {
        rUid,
        cUid,
        time,
      })
      .pipe(
        map(() => undefined)
      );
  }
}
